import type { GenericAbortSignal } from 'axios';

import type { TEmployeesApi } from '@/components/App/main-page/widgets/employees-summary/types.ts';
import { ApiConnector } from '@/integrations/api.connector';
import type { TBankSummaryFilters } from '@/store/bankSummary.slice';
import type { StrictOmit } from '@/types/app';
import type { TBankSummary } from '@/types/bankSummary.type';

import type { BanksSummaryInterface } from '../types/banks-summary';
import type { GetBaseSummaryProps } from '../types/base';
import type {
  ConversionRateInterface,
  ConversionRateProps
} from '../types/conversion-rate';
import type {
  BrandStatisticInterface,
  GetSalesSummaryProps,
  ModelsStatisticInterface,
  OnlineRequestsStatisticInterface,
  RequestsStatisticInterface
} from '../types/sales-summary';

class MainPageApi extends ApiConnector {
  getBrandsSummary(
    props: GetBaseSummaryProps,
    abortSignal?: GenericAbortSignal
  ): Promise<BrandStatisticInterface[]> {
    return this.call({
      path: 'requests/statistic/sales/brand',
      params: props,
      abortSignal
    });
  }

  getSalesSummary(
    props: GetSalesSummaryProps & { eachAuto: 1; brandUuid: string },
    abortSignal?: GenericAbortSignal | undefined
  ): Promise<ModelsStatisticInterface[]>;
  getSalesSummary(
    props: GetSalesSummaryProps & { eachAuto: 0 },
    abortSignal?: GenericAbortSignal | undefined
  ): Promise<RequestsStatisticInterface[]>;
  getSalesSummary(
    props: GetSalesSummaryProps,
    abortSignal?: GenericAbortSignal | undefined
  ) {
    if (props.eachAuto === 1)
      return this.call({
        path: 'requests/statistic/sales',
        params: props,
        abortSignal
      }) as Promise<ModelsStatisticInterface[]>;

    return this.call({
      path: 'request/statistic/sales',
      params: props,
      abortSignal
    }) as Promise<RequestsStatisticInterface[]>;
  }

  getSalesSummaryOnline(
    props: Pick<GetBaseSummaryProps, 'dateFrom' | 'dateTo'>,
    abortSignal?: GenericAbortSignal | undefined
  ) {
    return this.call({
      path: 'request/statistic/sales/online',
      params: props,
      abortSignal
    }) as Promise<[OnlineRequestsStatisticInterface]>;
  }

  getBanksSummary(
    props: GetBaseSummaryProps,
    abortSignal?: GenericAbortSignal | undefined
  ): Promise<BanksSummaryInterface> {
    return this.call({
      path: 'requests/statistic/sales/bank',
      params: props,
      abortSignal
    });
  }

  getBankSummary(filters: TBankSummaryFilters): Promise<[TBankSummary]> {
    return this.call({ path: 'request/statistic/sales', params: filters });
  }

  getBankSummaryTotal(
    filters: StrictOmit<TBankSummaryFilters, 'bankUuid'>
  ): Promise<[TBankSummary]> {
    return this.call({
      path: 'request/statistic/sales/summary',
      params: filters
    });
  }

  getEmployeesSummary(
    props: GetBaseSummaryProps,
    abortSignal?: GenericAbortSignal
  ): Promise<TEmployeesApi> {
    return this.call({
      path: 'requests/statistic/sales/user',
      params: props,
      abortSignal
    });
  }

  getConversionRate(
    props: ConversionRateProps,
    abortSignal?: GenericAbortSignal
  ): Promise<ConversionRateInterface[]> {
    return this.call({
      path: 'request/statistic/sales/convert',
      params: props,
      abortSignal
    });
  }
}

export const mainPageApi = new MainPageApi();
